import type { PatientPackageItem } from '@/services/packages.service';

/**
 * The procedures a package was sold with, for the package window's Procedures
 * section. Built from the package's own items (PatientPackagesController), so a
 * procedure shows whatever its state now: available, used, expired or deleted.
 */

export interface ProcedureGroup {
  key: string;
  name: string;
  category: string | null;
  items: PatientPackageItem[];
}

export type ProcedureStatus = 'available' | 'used' | 'expired' | 'deleted';

/** Legacy's package list names a procedure "Sub Category (Procedure)". */
export function procedureLabel(item: PatientPackageItem): string {
  if (item.subcategoryName && item.procedureName) return `${item.subcategoryName} (${item.procedureName})`;
  return item.procedureName ?? item.subcategoryName ?? item.itemName;
}

/** Every procedure row the package was sold with, grouped by procedure. */
export function groupPackageProcedures(items: PatientPackageItem[]): ProcedureGroup[] {
  const groups = new Map<string, ProcedureGroup>();
  for (const item of items) {
    if (item.itemType !== 'procedure') continue;
    const key = item.procSubId ? `proc-${item.procSubId}` : `name-${procedureLabel(item)}`;
    const group = groups.get(key) ?? { key, name: procedureLabel(item), category: item.categoryName ?? null, items: [] };
    group.items.push(item);
    groups.set(key, group);
  }
  return [...groups.values()];
}

/** Today in Pacific, where stored dates live, as 'YYYY-MM-DD'. */
export function pacificToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(now);
}

/**
 * An item is usable through the end of its expiry day, the rule the Remaining
 * Products filter and column use (ListFilterService::usablePackageItems).
 */
export function procedureStatus(item: PatientPackageItem, today: string): ProcedureStatus {
  if (item.usedDate) return 'used';
  if (item.deleteDate) return 'deleted';
  if (item.expireDate && item.expireDate.slice(0, 10) < today) return 'expired';
  return 'available';
}

/**
 * A stored day ('2026-10-22', or the date part of a Pacific wall time) as written,
 * with no timezone conversion: parsing it as UTC and showing it in Pacific would
 * move an expiry day back by one.
 */
export function fmtStoredDay(value: string | null | undefined): string {
  const m = (value ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
